import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import CountryAccessPicker from "../components/CountryAccessPicker";
import UserPermissionsPanel from "../components/UserPermissionsPanel";
import { crmHeaders } from "../utils/crmApi";
import { EMPTY_USER_PERMISSIONS } from "../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AddUser() {
    const navigate = useNavigate();
    const [role, setRole] = useState("");
    const [countryAccess, setCountryAccess] = useState([]);
    const [userPermissions, setUserPermissions] = useState({ ...EMPTY_USER_PERMISSIONS });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const name = formData.get("username");
        const email = formData.get("email");
        const password = formData.get("password");
        const selectedRole = formData.get("role");

        if (!name || !email || !password || !selectedRole) {
            toast.error("All fields are required!");
            return;
        }

        const payload = {
            Role: selectedRole,
            Name: name,
            Email: email,
            Password: password,
        };

        if (selectedRole === "User") {
            const selected = countryAccess.filter(
                (a) => a.leads || (Array.isArray(a.emailIds) && a.emailIds.length > 0)
            );
            if (!selected.length) {
                toast.error("Select at least one country and permission for this user");
                return;
            }
            payload.CountryAccess = selected;
            payload.UserPermissions = userPermissions;
        }

        setSubmitting(true);
        axios
            .post(`${BASE_URL}/adduser`, payload, { headers: crmHeaders() })
            .then((res) => {
                if (res.data.message === "Username already exist") {
                    toast.error("This username already exists!");
                } else {
                    toast.success("User created successfully");
                    navigate("/users");
                }
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Failed to create user");
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <div className="container crm-user-form-page">
            <ToastContainer />
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Create New User</h1>
                    <p className="crm-page-subtitle">
                        Add team member with country access and feature permissions
                    </p>
                </div>
                <Link to="/users" className="btn btn-outline-secondary">
                    <i className="bi bi-arrow-left me-1"></i> Back to Users
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="crm-user-form">
                <div className="crm-user-form-card">
                    <div className="crm-user-form-card-header">
                        <i className="bi bi-person-badge"></i>
                        <div>
                            <h5>Account Details</h5>
                            <span>Basic login information for the new user</span>
                        </div>
                    </div>
                    <div className="crm-user-form-card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    placeholder="Enter username"
                                    className="form-control"
                                    name="username"
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">
                                    Email <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    placeholder="user@example.com"
                                    className="form-control"
                                    name="email"
                                    required
                                />
                                <small className="text-muted">Used for OTP login verification</small>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Role</label>
                                <select
                                    className="form-select"
                                    name="role"
                                    required
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                >
                                    <option value="" disabled>
                                        Select role
                                    </option>
                                    <option value="Administrator">Administrator</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    placeholder="8–20 characters"
                                    className="form-control"
                                    name="password"
                                    required
                                    minLength={8}
                                    maxLength={20}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {role === "User" && (
                    <>
                        <div className="crm-user-form-card">
                            <div className="crm-user-form-card-header">
                                <i className="bi bi-globe2"></i>
                                <div>
                                    <h5>Country Access</h5>
                                    <span>Leads and inbox email accounts per country</span>
                                </div>
                            </div>
                            <div className="crm-user-form-card-body">
                                <CountryAccessPicker access={countryAccess} onChange={setCountryAccess} />
                            </div>
                        </div>

                        <div className="crm-user-form-card">
                            <div className="crm-user-form-card-body pt-0">
                                <UserPermissionsPanel
                                    permissions={userPermissions}
                                    onChange={setUserPermissions}
                                />
                            </div>
                        </div>
                    </>
                )}

                <div className="crm-user-form-actions">
                    <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
                        {submitting ? "Creating..." : "Create User"}
                    </button>
                    <Link to="/users" className="btn btn-outline-secondary btn-lg">
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}
